import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { PublicNav, PublicFooter } from "../../components/public/PublicNav";
import { useSeo } from "../../hooks/useSeo";
import { Send, CheckCircle2 } from "lucide-react";

export const PublicContact = () => {
  useSeo({
    title: "Contact — Écrire à la rédaction | Rouge Première",
    description: "Contactez la rédaction de Rouge Première : partenariats, presse, suggestions et remarques sur l'agenda cinéma & séries.",
  });
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message envoyé — merci !");
    } catch (e) {
      const detail =
        e?.response?.data?.detail?.[0]?.msg ||
        e?.response?.data?.detail ||
        "Erreur d'envoi. Réessayez dans un instant.";
      setErr(typeof detail === "string" ? detail : "Erreur d'envoi.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <PublicNav />
      <main className="max-w-[860px] mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="contact-page">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)]">Écrire à la rédaction</div>
        <h1 className="font-display text-4xl md:text-5xl mt-3">Contact</h1>
        <p className="mt-4 text-neutral-600 max-w-2xl leading-relaxed">
          Une remarque, une suggestion d'article, un partenariat, une correction — utilisez ce
          formulaire, nous lisons chaque message.
        </p>

        {sent ? (
          <div
            data-testid="contact-success"
            className="mt-10 rounded-lg border border-emerald-200 bg-emerald-50 p-8 flex gap-4 items-start"
          >
            <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-2xl">Message envoyé</div>
              <p className="text-sm text-neutral-700 mt-2">
                Merci pour votre message. Nous revenons vers vous rapidement.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="contact-form">
            {/* Honeypot: invisible for humans */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={setField("website")}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute -left-[9999px] w-1 h-1 opacity-0"
            />

            <Field label="Nom" required>
              <input
                data-testid="contact-name"
                type="text"
                required
                maxLength={120}
                value={form.name}
                onChange={setField("name")}
                className="input"
              />
            </Field>

            <Field label="Email" required>
              <input
                data-testid="contact-email"
                type="email"
                required
                value={form.email}
                onChange={setField("email")}
                className="input"
              />
            </Field>

            <Field label="Sujet" required>
              <input
                data-testid="contact-subject"
                type="text"
                required
                maxLength={180}
                value={form.subject}
                onChange={setField("subject")}
                className="input"
              />
            </Field>

            <Field label="Message" required>
              <textarea
                data-testid="contact-message"
                required
                minLength={5}
                maxLength={5000}
                rows={7}
                value={form.message}
                onChange={setField("message")}
                className="input resize-y"
              />
            </Field>

            {err && (
              <div data-testid="contact-error" className="text-sm text-[var(--red)]">
                {err}
              </div>
            )}

            <div className="pt-2">
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="btn-red disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
                {sending ? "Envoi…" : "Envoyer le message"}
              </button>
            </div>

            <p className="text-xs text-neutral-500 mt-3">
              Vos coordonnées ne sont utilisées que pour vous répondre. Voir nos{" "}
              <a href="/mentions-legales" className="underline hover:text-[var(--red)]">
                mentions légales
              </a>.
            </p>
          </form>
        )}
      </main>
      <PublicFooter />
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
      {label}
      {required && <span className="text-[var(--red)] ml-1">*</span>}
    </span>
    <div className="mt-1.5">{children}</div>
  </label>
);
